import React from 'react';
import type { Assignment, Technician } from '@shared/types';
import { CheckCircle, Clock, Users } from 'lucide-react';
import { format } from 'date-fns';

interface PendingClosuresProps {
    technicians: Technician[];
    onCloseWorksite: (assignmentId: string) => void;
}

export const PendingClosures: React.FC<PendingClosuresProps> = ({
    technicians,
    onCloseWorksite,
}) => {
    // Get all assignments that are completed by technicians but not yet closed
    const pendingClosures: Array<{ assignment: Assignment; technician: Technician }> = [];

    technicians.forEach(tech => {
        tech.assignments.forEach(assignment => {
            if (assignment.status === 'completed-by-tech') {
                pendingClosures.push({ assignment, technician: tech });
            }
        });
    });

    // Sort by completion date, most recent first
    pendingClosures.sort((a, b) => {
        const dateA = a.assignment.completedAt || '';
        const dateB = b.assignment.completedAt || '';
        return dateB.localeCompare(dateA);
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-100">Chantiers en attente de clôture</h2>
                    <p className="text-sm text-gray-400">
                        {pendingClosures.length} chantier{pendingClosures.length !== 1 ? 's' : ''} terminé{pendingClosures.length !== 1 ? 's' : ''} par les techniciens
                    </p>
                </div>
            </div>

            {pendingClosures.length === 0 ? (
                <div className="glass-panel p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-400">Aucun chantier en attente de clôture</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {pendingClosures.map(({ assignment, technician }) => (
                        <div
                            key={`${technician.id}-${assignment.id}`}
                            className="glass-panel p-5 border border-yellow-500/30"
                        >
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-100 text-lg mb-1">{assignment.clientName}</h3>
                                    {assignment.task && (
                                        <p className="text-sm text-gray-400 mb-2">{assignment.task}</p>
                                    )}

                                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                                        <span>
                                            📅 {format(new Date(assignment.startDate), 'dd/MM/yyyy')} - {format(new Date(assignment.endDate), 'dd/MM/yyyy')}
                                        </span>
                                        {assignment.city && (
                                            <span>📍 {assignment.city}</span>
                                        )}
                                    </div>

                                    {/* Technician(s) */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <Users className="w-4 h-4 text-gray-500" />
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="px-2 py-1 rounded text-xs font-medium text-white"
                                                style={{ backgroundColor: technician.color }}
                                            >
                                                {technician.name}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Timestamps */}
                                    <div className="space-y-1 text-xs text-gray-400 bg-gray-800/30 rounded-lg p-3">
                                        {assignment.startedAt && (
                                            <div>
                                                <span className="text-gray-500">Démarré :</span> {format(new Date(assignment.startedAt), 'dd/MM/yyyy à HH:mm')}
                                            </div>
                                        )}
                                        {assignment.completedAt && (
                                            <div>
                                                <span className="text-gray-500">Terminé :</span> {format(new Date(assignment.completedAt), 'dd/MM/yyyy à HH:mm')}
                                            </div>
                                        )}
                                    </div>

                                    {/* Remarque */}
                                    {assignment.remarque && (
                                        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                            <div className="text-xs font-medium text-blue-300 mb-1">Remarques du technicien:</div>
                                            <div className="text-sm text-gray-300">{assignment.remarque}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Button */}
                            <button
                                onClick={() => onCloseWorksite(assignment.id)}
                                className="w-full glass-button bg-green-600/20 border-green-600/50 text-green-400 hover:bg-green-600/30 flex items-center justify-center gap-2 py-3"
                            >
                                <CheckCircle className="w-5 h-5" />
                                Valider et clôturer le chantier
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
